import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, moneyAmount } from '../../stores/coreSession';
import { fragmentCount } from '../fragmentModel';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const VECTORS = [
  { id: 'liquidate', title: 'LIQUIDATE', lines: ['FENCE SALE', '200 BPS FEE', 'CASH / NO TRACE'] },
  { id: 'archive', title: 'ARCHIVE', lines: ['4 COAT SLOTS', '+8 HEAT / DAY', 'POINTS AT DAY 31'] },
  { id: 'expose', title: 'EXPOSE', lines: ['-50 HEAT', '+25 REP', '1.5X LOCAL SPIKE'] },
];

const state = { hronir: null, fragments: [], loaded: false, selected: -1, loading: false, error: null, notice: '' };

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function held() {
  return state.hronir && ['held', 'archived'].includes(state.hronir.status);
}

function coreCash() {
  const core = useCoreSession.getState().state || {};
  return moneyAmount(core.cash);
}

async function loadHronir(force = false) {
  if (state.loading && !force) return;
  state.loading = true;
  state.error = null;
  try {
    const [items, fragments] = await Promise.all([
      useCoreSession.getState().loadHronir(),
      useCoreSession.getState().loadFragments().catch(() => []),
    ]);
    state.hronir = (items || [])[0] || null;
    state.fragments = fragments || [];
    state.loaded = true;
  } catch (error) {
    state.error = coreErrorMessage(error);
  } finally {
    state.loading = false;
  }
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // HRONIR', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'SUGRU_9 // EXPECTATION PAYLOAD', 232, 5, TERMINAL.TEXT_DIM);
  text(ctx, held() ? 'HELD' : (session.status === 'online' ? 'LIVE' : 'OFFLINE'), width - 64, 5, held() ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawDossier(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'ARTIFACT // SUGRU_9');
  text(ctx, 'PROVENANCE', x + 12, y + 30, TERMINAL.TEXT_DIM);
  text(ctx, 'LLM hallucination / Reddit expectation', x + 12, y + 48, TERMINAL.TEXT);
  text(ctx, 'CLASS', x + 12, y + 74, TERMINAL.TEXT_DIM);
  text(ctx, 'HRONIR / OFF-INDEX', x + 12, y + 92, TERMINAL.TEXT);
  text(ctx, 'SEED', x + 12, y + 118, TERMINAL.TEXT_DIM);
  text(ctx, state.hronir?.seed?.amount ? `$${state.hronir.seed.amount}` : '$600.00', x + 152, y + 118, TERMINAL.TEXT);
  text(ctx, 'VALUATION', x + 12, y + 138, TERMINAL.TEXT_DIM);
  text(ctx, state.hronir?.valuation?.amount ? `$${state.hronir.valuation.amount}` : '--', x + 152, y + 138, TERMINAL.TEXT);
  text(ctx, 'STATUS', x + 12, y + 158, TERMINAL.TEXT_DIM);
  text(ctx, state.hronir?.status?.toUpperCase() || '--', x + 152, y + 158, TERMINAL.TEXT);
  text(ctx, 'EXPEDIENTE', x + 12, y + 178, TERMINAL.TEXT_DIM);
  text(ctx, `${fragmentCount(state.fragments)}/4 // TURNS ${state.hronir?.turnsArchived ?? 0}`, x + 152, y + 178, TERMINAL.TEXT);
  rule(ctx, x + 12, y + 192, w - 24);
  text(ctx, state.error || state.notice || 'THE VALUE IS SOCIAL.', x + 12, y + 204, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
}

function drawVerbs(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'THREE VECTORS');
  const session = useCoreSession.getState();
  const online = session.status === 'online';
  const hasHronir = held();
  const rowH = 54;
  VECTORS.forEach((block, index) => {
    const rowY = y + 28 + index * rowH;
    const selected = state.selected === index;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 3, w - 12, rowH - 6);
    }
    text(ctx, `[${index + 1}] ${block.title}`, x + 12, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    block.lines.forEach((line, i) => text(ctx, line, x + 20, rowY + 16 + i * 13, TERMINAL.TEXT_DIM));
    interactiveRegistry.register({
      id: `hronir-verb-${block.id}`,
      x: x + 6,
      y: rowY - 3,
      w: w - 12,
      h: rowH - 6,
      onClick: () => {
        state.selected = index;
        state.notice = '';
      },
    });
  });

  const selected = state.selected >= 0 ? VECTORS[state.selected].title : 'NONE';
  text(ctx, `SELECTED: ${selected}`, x + 12, y + 190, state.selected >= 0 ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);

  if (!hasHronir) {
    const canAcquire = online && coreCash() >= 600 && !state.loading;
    DopeUI.Button(ctx, x + 12, y + 226, w - 24, 44, state.loading ? 'ACQUIRING' : 'ACQUIRE $600', canAcquire ? 'hover' : 'disabled');
    text(ctx, canAcquire ? 'SEED PAYMENT // CASH OR LOAN' : 'NEED $600 // LOAN VIA VINNIE', x + 12, y + 208, TERMINAL.TEXT_DIM);
    if (canAcquire) {
      interactiveRegistry.register({
        id: 'hronir-acquire',
        x: x + 12,
        y: y + 226,
        w: w - 24,
        h: 44,
        onClick: async () => {
          state.loading = true;
          state.error = null;
          try {
            await useCoreSession.getState().acquireHronir();
            state.notice = 'SUGRU_9 ACQUIRED';
            await loadHronir(true);
          } catch (error) {
            state.error = coreErrorMessage(error);
          } finally {
            state.loading = false;
          }
        },
      });
    }
    return;
  }

  const canConfirm = online && state.selected >= 0 && !state.loading;
  DopeUI.Button(ctx, x + 12, y + 226, w - 24, 44, state.loading ? 'SENDING' : 'CONFIRM VECTOR', canConfirm ? 'hover' : 'disabled');
  text(ctx, canConfirm ? 'IRREVERSIBLE // ARCHIVE VERB' : 'SELECT A VECTOR FIRST', x + 12, y + 208, TERMINAL.TEXT_DIM);
  if (canConfirm) {
    interactiveRegistry.register({
      id: 'hronir-confirm',
      x: x + 12,
      y: y + 226,
      w: w - 24,
      h: 44,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        try {
          const verb = VECTORS[state.selected].id;
          const result = await useCoreSession.getState().hronirVerb(state.hronir.hronirId, verb);
          state.notice = `${verb.toUpperCase()} // ${result.hronir?.status?.toUpperCase() || 'SENT'}`;
          await loadHronir(true);
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

registerView('hronir', function renderHronir(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  if (!state.loaded && !state.loading && useCoreSession.getState().status === 'online') {
    loadHronir();
  }

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawDossier(ctx, 8, 32, safe.width - 16, 240);
    drawVerbs(ctx, 8, 280, safe.width - 16, 280);
  } else {
    drawDossier(ctx, 8, 36, 376, 280);
    drawVerbs(ctx, 400, 36, 232, 280);
  }

  const footerY = mobile ? 570 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  if (!mobile) text(ctx, 'TAB FOCUS   ENTER SELECT   ESC DESKTOP', 8, 360, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 590 : 336;
  const backW = mobile ? safe.width - 16 : 192;
  const backH = mobile ? 44 : 24;
  DopeUI.Button(ctx, backX, backY, backW, backH, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'hronir-back', x: backX, y: backY, w: backW, h: backH,
    onClick: () => navigateTo('main_menu'),
  });
  const footer = state.error
    || state.notice
    || (held()
      ? `${VECTORS[state.selected]?.title || 'NO VECTOR'} SELECTED // VECTOR`
      : 'ACQUIRE SUGRU_9 // THREE VECTORS');
  text(ctx, footer, 8, mobile ? footerY + 10 : 384, state.error ? TERMINAL.TEXT : TERMINAL.GREEN);
});
